/**
 * Shared opening-hours system used by every pass layout.
 *
 * Hours are stored as structured weekly data so we can compute open/closed
 * status, "closes in 2h", and "opens Tue 12pm" reliably — instead of parsing
 * loose strings like "5:30pm–11pm Tue–Sun".
 *
 * Times are 24h "HH:mm". A close time after midnight is expressed as "25:00"
 * meaning 1am the next day (so a bar open 5pm–1am is ["17:00","25:00"]).
 *
 * WeeklyHours shape: { sun?: [ [open, close], ... ], mon?: ..., ..., sat?: ... }
 * where each range is a [open, close] tuple of "HH:mm" strings (close may
 * exceed "24:00" for overnight ranges).
 */

const DAY_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const DAY_LABEL = {
  sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat'
}
const DAY_LONG = {
  sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday',
  thu: 'Thursday', fri: 'Friday', sat: 'Saturday'
}

// --- helpers ---------------------------------------------------------------

const toMin = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + (m || 0)
}

/** "17:00" -> "5pm", "17:30" -> "5:30pm", "25:00" -> "1am". */
export const fmtTime = (hhmm) => {
  let mins = toMin(hhmm)
  mins = ((mins % (24 * 60)) + 24 * 60) % (24 * 60)
  const h24 = Math.floor(mins / 60)
  const m = mins % 60
  const am = h24 < 12
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return m === 0 ? `${h12}${am ? 'am' : 'pm'}` : `${h12}:${String(m).padStart(2, '0')}${am ? 'am' : 'pm'}`
}

const sameRanges = (a, b) =>
  a.length === b.length && a.every((r, i) => r[0] === b[i][0] && r[1] === b[i][1])

/** "5:30pm–11pm Tue–Sun · closed Mon" — compact week summary. */
export const summarizeWeek = (weekly) => {
  const groups = []
  for (const d of DAY_ORDER) {
    const ranges = weekly[d] ?? null
    const last = groups[groups.length - 1]
    if (last && ((!last.ranges && !ranges) || (last.ranges && ranges && sameRanges(last.ranges, ranges)))) {
      last.days.push(d)
    } else {
      groups.push({ days: [d], ranges })
    }
  }
  const open = groups.filter((g) => g.ranges)
  const closed = groups.filter((g) => !g.ranges).flatMap((g) => g.days)
  const fmtGroup = (g) => {
    const span = g.days.length === 1 ? DAY_LABEL[g.days[0]] : `${DAY_LABEL[g.days[0]]}–${DAY_LABEL[g.days[g.days.length - 1]]}`
    const r = g.ranges.map(([a, b]) => `${fmtTime(a)}–${fmtTime(b)}`).join(', ')
    return `${r} ${span}`
  }
  const parts = open.map(fmtGroup)
  if (closed.length) parts.push(`closed ${closed.map((d) => DAY_LABEL[d]).join(', ')}`)
  return parts.join(' · ')
}

/** Today-only label: "12pm–8pm" or "Closed today". */
export const todayLabel = (weekly, now = new Date()) => {
  const d = DAY_ORDER[now.getDay()]
  const ranges = weekly[d]
  if (!ranges?.length) return 'Closed today'
  return ranges.map(([a, b]) => `${fmtTime(a)}–${fmtTime(b)}`).join(', ')
}

// --- status ---------------------------------------------------------------

/**
 * Returns { isOpen, label } — label is a short pill string,
 * e.g. "Open · closes 8pm" or "Closed · opens Tue 12pm".
 */
export const nowInZone = (tz) =>
  tz ? new Date(new Date().toLocaleString('en-US', { timeZone: tz })) : new Date()

export const getOpenStatus = (weekly, now = new Date()) => {
  const dowIdx = now.getDay()
  const nowMin = now.getHours() * 60 + now.getMinutes()

  // Check today (and overnight from yesterday)
  const todayKey = DAY_ORDER[dowIdx]
  const yKey = DAY_ORDER[(dowIdx + 6) % 7]

  // Overnight from yesterday: e.g. yesterday 17:00–25:00 -> today 0:00–01:00
  for (const [, b] of weekly[yKey] ?? []) {
    const closeMin = toMin(b)
    if (closeMin > 24 * 60) {
      const todayClose = closeMin - 24 * 60
      if (nowMin < todayClose) {
        return { isOpen: true, label: `Open · closes ${fmtTime(b)}` }
      }
    }
  }

  // Today's ranges
  for (const [a, b] of weekly[todayKey] ?? []) {
    const o = toMin(a)
    const c = toMin(b)
    if (nowMin >= o && nowMin < c) {
      return { isOpen: true, label: `Open · closes ${fmtTime(b)}` }
    }
    if (nowMin < o) {
      return { isOpen: false, label: `Closed · opens ${fmtTime(a)}` }
    }
  }

  // Look ahead up to 7 days for the next opening
  for (let i = 1; i <= 7; i++) {
    const k = DAY_ORDER[(dowIdx + i) % 7]
    const ranges = weekly[k]
    if (ranges?.length) {
      const [a] = ranges[0]
      const dayName = i === 1 ? 'tomorrow' : DAY_LABEL[k]
      return { isOpen: false, label: `Closed · opens ${dayName} ${fmtTime(a)}` }
    }
  }
  return { isOpen: false, label: 'Closed' }
}

/** Full week as rows for the popover. Highlights today. */
export const weekRows = (weekly, now = new Date()) => {
  const todayKey = DAY_ORDER[now.getDay()]
  return DAY_ORDER.map((d) => ({
    key: d,
    label: DAY_LONG[d],
    isToday: d === todayKey,
    text: weekly[d]?.length
      ? weekly[d].map(([a, b]) => `${fmtTime(a)}–${fmtTime(b)}`).join(', ')
      : 'Closed'
  }))
}

// --- presets ---------------------------------------------------------------

const everyDay = (ranges) =>
  Object.fromEntries(DAY_ORDER.map((d) => [d, ranges]))

const onlyDays = (days, ranges) =>
  Object.fromEntries(days.map((d) => [d, ranges]))

/** Common opening-hours archetypes — assign these to venues. */
export const HOURS = {
  /** 9am–9pm every day. */
  allDay: everyDay([['09:00', '21:00']]),
  /** 8am–8pm every day. */
  daytime: everyDay([['08:00', '20:00']]),
  /** Cafe / bakery: 7am–3pm every day. */
  cafe: everyDay([['07:00', '15:00']]),
  /** Brunch: 9am–3pm Wed–Sun. */
  brunchWedSun: onlyDays(['wed', 'thu', 'fri', 'sat', 'sun'], [['09:00', '15:00']]),
  /** Lunch + dinner, closed Monday. */
  lunchDinnerCloseMon: {
    tue: [['11:30', '14:30'], ['17:00', '22:00']],
    wed: [['11:30', '14:30'], ['17:00', '22:00']],
    thu: [['11:30', '14:30'], ['17:00', '22:00']],
    fri: [['11:30', '14:30'], ['17:00', '23:00']],
    sat: [['11:30', '14:30'], ['17:00', '23:00']],
    sun: [['11:30', '21:00']]
  },
  /** Dinner only Tue–Sun, closed Mon. */
  dinnerTueSun: onlyDays(['tue', 'wed', 'thu', 'fri', 'sat', 'sun'], [['17:00', '22:00']]),
  /** Dinner only every day. */
  dinnerDaily: everyDay([['17:00', '22:00']]),
  /** Late dinner Tue–Sun: 5:30pm–11pm. */
  dinnerLateTueSun: onlyDays(['tue', 'wed', 'thu', 'fri', 'sat', 'sun'], [['17:30', '23:00']]),
  /** Brewery / taproom: 12pm–10pm daily, Fri/Sat to midnight. */
  brewery: {
    sun: [['12:00', '21:00']],
    mon: [['15:00', '22:00']],
    tue: [['15:00', '22:00']],
    wed: [['12:00', '22:00']],
    thu: [['12:00', '22:00']],
    fri: [['12:00', '24:00']],
    sat: [['12:00', '24:00']]
  },
  /** Bar: 4pm–1am (overnight) daily. */
  bar: everyDay([['16:00', '25:00']]),
  /** Wine bar: 5pm–11pm Tue–Sun. */
  wineBarTueSun: onlyDays(['tue', 'wed', 'thu', 'fri', 'sat', 'sun'], [['17:00', '23:00']]),
  /** Museum / shop: 10am–6pm Tue–Sun. */
  museumTueSun: onlyDays(['tue', 'wed', 'thu', 'fri', 'sat', 'sun'], [['10:00', '18:00']]),
  /** Retail: 10am–7pm daily. */
  retail: everyDay([['10:00', '19:00']]),
  /** Distillery / tasting room: 12pm–8pm daily. */
  tastingRoom: everyDay([['12:00', '20:00']]),
  /** Cidery / winery: 1pm–9pm daily. */
  cidery: everyDay([['13:00', '21:00']]),
  /** Closed (event venue, by appointment). */
  byAppointment: {}
}

// --- string parser (best-effort fallback) ---------------------------------

const DAY_TOKENS = {
  sun: 'sun', mon: 'mon', tue: 'tue', tues: 'tue', wed: 'wed', weds: 'wed',
  thu: 'thu', thur: 'thu', thurs: 'thu', fri: 'fri', sat: 'sat'
}

const parseClock = (s) => {
  const m = s.trim().toLowerCase().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|noon|midnight)?$/)
  if (!m) return null
  if (m[3] === 'noon') return '12:00'
  if (m[3] === 'midnight') return '24:00'
  let h = parseInt(m[1], 10)
  const mins = m[2] ? parseInt(m[2], 10) : 0
  const ap = m[3]
  if (ap === 'pm' && h < 12) h += 12
  if (ap === 'am' && h === 12) h = 0
  return `${String(h).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

const parseDayPart = (s) => {
  const cleaned = s.toLowerCase().trim()
  if (!cleaned || /daily|every ?day|7 ?days/.test(cleaned)) return [...DAY_ORDER]
  if (/weekend/.test(cleaned)) return ['sat', 'sun']
  if (/weekday/.test(cleaned)) return ['mon', 'tue', 'wed', 'thu', 'fri']
  // Range "Tue–Sun" / "Tue-Sun" / "Tue to Sun"
  const range = cleaned.match(/([a-z]+)\s*[–\-to]+\s*([a-z]+)/)
  if (range) {
    const a = DAY_TOKENS[range[1].slice(0, 4)] ?? DAY_TOKENS[range[1].slice(0, 3)]
    const b = DAY_TOKENS[range[2].slice(0, 4)] ?? DAY_TOKENS[range[2].slice(0, 3)]
    if (a && b) {
      const ai = DAY_ORDER.indexOf(a)
      const bi = DAY_ORDER.indexOf(b)
      const out = []
      for (let i = 0; i < 7; i++) {
        const idx = (ai + i) % 7
        out.push(DAY_ORDER[idx])
        if (idx === bi) break
      }
      return out
    }
  }
  // Comma list: "Mon, Wed, Fri"
  const tokens = cleaned.split(/[,\s]+/).map((t) => DAY_TOKENS[t.slice(0, 4)] ?? DAY_TOKENS[t.slice(0, 3)]).filter(Boolean)
  return tokens.length ? tokens : [...DAY_ORDER]
}

/**
 * Best-effort parser for strings like:
 *   "12pm–8pm"
 *   "5:30pm–11pm Tue–Sun"
 *   "9am–5pm Mon–Fri"
 *   "Daily 10am–6pm"
 * Returns {} if unparseable — caller should treat as no-data.
 */
export const parseHoursString = (raw) => {
  if (!raw) return {}
  const s = raw.replace(/–|—/g, '–').trim()
  // Find a time range "Hh(:mm)?(am|pm)–Hh(:mm)?(am|pm)"
  const tm = s.match(/(\d{1,2}(?::\d{2})?\s*(?:am|pm|noon|midnight)?)\s*[–\-to]+\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm|noon|midnight)?)/i)
  if (!tm) return {}
  const open = parseClock(tm[1])
  let close = parseClock(tm[2])
  if (!open || !close) return {}
  // Overnight: close <= open → add 24h
  if (toMin(close) <= toMin(open)) {
    const [h, m] = close.split(':').map(Number)
    close = `${String(h + 24).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`
  }
  const dayPart = s.slice(s.indexOf(tm[0]) + tm[0].length)
  const days = parseDayPart(dayPart)
  const range = [open, close]
  return Object.fromEntries(days.map((d) => [d, [range]]))
}

/** Resolve weekly hours from an explicit weekly object or by parsing a string. */
export const resolveWeekly = (weekly, hoursString) => {
  if (weekly && Object.keys(weekly).length) return weekly
  return parseHoursString(hoursString)
}
